import { z } from "zod";

/**
 * "Load earlier conversation" (PREC-4305): a lead's history on the viewing user's own LinkedIn
 * account and mailboxes, pulled into the chat on demand.
 *
 * Imported messages are history only. They live in their own table (`imported_conversation`),
 * never in `conversation`, so no reply predicate, KPI, sequence stop, sentiment run, CRM sync or
 * lead status can see them — the API reads them in exactly two places: the chat log (this
 * payload) and the AI drafting context.
 *
 * Who sees them (Andreas, 2026-10-10): imported on a lead that is not private, they are shared
 * with the team like the lead's synced history; imported on a private lead (a PRIVATE list, or a
 * privately owned prospect), they stay the importer's alone.
 */

/** The direction a message is stored with; the same words a synced `conversation` row uses. */
export const IMPORTED_CONVERSATION_TYPES = [
	"message_sent",
	"message_received",
	"mail_sent",
	"mail_received",
] as const;

export type ImportedConversationType = (typeof IMPORTED_CONVERSATION_TYPES)[number];

/** The two places a history is read from. */
export const CONVERSATION_HISTORY_SOURCES = ["LINKEDIN", "EMAIL"] as const;

export type ConversationHistorySource = (typeof CONVERSATION_HISTORY_SOURCES)[number];

/**
 * What one source answered. Every value is a different sentence for the user, and `FAILED` is
 * never one of the "nothing there" answers: a lookup that broke is not a lookup that found
 * nothing.
 *
 * - `IMPORTED`         — new messages were added (`imported` > 0).
 * - `UP_TO_DATE`       — messages were found, and every one was already in the chat.
 * - `NOTHING_FOUND`    — the lookup ran and there is no earlier conversation.
 * - `NOT_CONNECTED`    — the viewing user has no connected LinkedIn account / mailbox.
 * - `NO_LEAD_IDENTITY` — the lead has no LinkedIn profile / email address to look for.
 * - `FAILED`           — the provider could not be read; rows already imported stay.
 * - `BLOCKED`          — this environment may not reach the provider (`BLOCK_UNIPILE`, aw-qa):
 *                        nothing was asked, so nothing can be said about the history.
 */
export const CONVERSATION_HISTORY_STATUSES = [
	"IMPORTED",
	"UP_TO_DATE",
	"NOTHING_FOUND",
	"NOT_CONNECTED",
	"NO_LEAD_IDENTITY",
	"FAILED",
	"BLOCKED",
] as const;

export type ConversationHistoryStatus = (typeof CONVERSATION_HISTORY_STATUSES)[number];

export const conversationHistorySourceResultSchema = z.object({
	status: z.enum(CONVERSATION_HISTORY_STATUSES),
	/** Messages added by this run. */
	imported: z.number(),
	/** Messages the lookup matched to the lead, including the ones already in the chat. */
	found: z.number(),
});

export type ConversationHistorySourceResult = z.infer<typeof conversationHistorySourceResultSchema>;

export const conversationImportResultSchema = z.object({
	linkedin: conversationHistorySourceResultSchema,
	email: conversationHistorySourceResultSchema,
});

export type ConversationImportResult = z.infer<typeof conversationImportResultSchema>;

/** One imported message, shaped like the chat log's conversation rows. */
export const importedConversationSchema = z.object({
	id: z.number(),
	type: z.enum(IMPORTED_CONVERSATION_TYPES),
	message: z.string().nullable(),
	subject: z.string().nullable(),
	sentAt: z.string(),
	importedAt: z.string(),
	userId: z.number(),
	/** The colleague who imported it; null when the user no longer exists. */
	importedBy: z
		.object({ id: z.number(), firstName: z.string().nullable(), lastName: z.string().nullable() })
		.nullable(),
	/** True when it was imported on a private lead and is the importer's alone. */
	isPrivate: z.boolean(),
	emailId: z.number().nullable(),
	senderEmail: z.string().nullable(),
	receiverEmail: z.string().nullable(),
	senderName: z.string().nullable(),
	senderLinkedinUrl: z.string().nullable(),
	receiverLinkedinUrl: z.string().nullable(),
});

export type ImportedConversation = z.infer<typeof importedConversationSchema>;

export const importedConversationListSchema = z.array(importedConversationSchema);

/**
 * The most imported messages the chat log shows for one lead, newest kept. The API caps its page
 * with it and the web names it when older messages were left out.
 */
export const IMPORTED_CONVERSATION_HISTORY_LIMIT = 200;

/** The chat log's imported history: the newest rows, oldest first; `truncated` when older were left out. */
export const importedConversationHistorySchema = z.object({
	items: importedConversationListSchema,
	truncated: z.boolean(),
	/** Whether what the viewer imports on this lead now is shared with the team (not a private lead). */
	shared: z.boolean(),
});

export type ImportedConversationHistory = z.infer<typeof importedConversationHistorySchema>;

/**
 * The status a source ends with, from what its lookup saw. Shared so the API decides it and the
 * web's wording is keyed on the same closed set.
 */
export function conversationHistoryStatusFor(input: {
	failed: boolean;
	found: number;
	imported: number;
}): ConversationHistoryStatus {
	if (input.failed) {
		return "FAILED";
	}

	if (input.imported > 0) {
		return "IMPORTED";
	}

	return input.found > 0 ? "UP_TO_DATE" : "NOTHING_FOUND";
}
